import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { fmtINR, fmtWhen } from "@/lib/service";
import { WorkshopPicker } from "@/components/WorkshopPicker";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Clock } from "lucide-react";

const TH = "px-2.5 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";
const BUCKETS = [["all", "All ages"], ["d0", "< 1 day"], ["d1_3", "1–3 days"], ["d3_7", "3–7 days"], ["d7", "7+ days"]];
const bucketOf = (age) => (age == null ? "unknown" : age < 1 ? "d0" : age < 3 ? "d1_3" : age < 7 ? "d3_7" : "d7");
const ageTone = (age) => (age == null ? "" : age >= 7 ? "text-red-600 font-bold" : age >= 3 ? "text-amber-700 font-semibold" : "");
const fmtDt = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)} ${s.slice(11, 16)}` : "—");

function Chip({ active, onClick, children, testId }) {
  return <button onClick={onClick} className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${active ? "bg-foreground text-background border-foreground" : "hover:bg-secondary"}`} data-testid={testId}>{children}</button>;
}

export default function ServiceOpenRos() {
  const [params, setParams] = useSearchParams();
  const workshop = params.get("workshop") || "";
  const [data, setData] = useState(null);
  const [bucket, setBucket] = useState("all");
  const [stage, setStage] = useState("");
  const [onlyBreached, setOnlyBreached] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    setData(null);
    api.get(`/service/open-ros?${qs({ workshop })}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
  }, [workshop]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.rows.filter((r) => (bucket === "all" || bucketOf(r.age_days) === bucket) && (!stage || r.stage === stage) && (!onlyBreached || r.breached)
      && (!needle || [r.ro_no, r.reg_no, r.model, r.sa, r.tech, r.customer, r.delay_reason].some((v) => (v || "").toLowerCase().includes(needle))));
  }, [data, bucket, stage, onlyBreached, q]);

  const s = data?.summary;
  return (
    <div className="space-y-5" data-testid="service-open-ros">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Open RO Board</h1>
          <p className="text-sm text-muted-foreground">Every vehicle still in the workshop · age, promise time, stage and delay reason{data?.snapshot_at && <> · RO list snapshot <b>{fmtWhen(data.snapshot_at)}</b></>}</p>
        </div>
        <WorkshopPicker workshops={data?.workshops} value={workshop} navigateTo={false} onChange={(v) => setParams(v ? { workshop: v } : {})} testId="oro-workshop-picker" />
      </div>

      {s && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-6">
          {[
            ["Open ROs", s.open, "", "oro-kpi-open"],
            ["Promise breached", s.breached, s.breached ? "text-red-600" : "", "oro-kpi-breached"],
            ["Breached, no reason", s.breached_no_reason, s.breached_no_reason ? "text-red-600" : "", "oro-kpi-noreason"],
            ["Aged 3+ days", s.aged_3plus, s.aged_3plus ? "text-amber-700" : "", "oro-kpi-aged"],
            ["Billed, not delivered", s.billed_not_delivered, "", "oro-kpi-bnd"],
            ["Estimate value", `₹${fmtINR(s.estimate_value)}`, "", "oro-kpi-estimate"],
          ].map(([l, v, tone, id]) => (
            <div key={l} className="border bg-card rounded-md p-3" data-testid={id}><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{l}</div><div className={`font-heading text-xl font-extrabold num mt-0.5 ${tone}`}>{v}</div></div>
          ))}
        </div>
      )}

      {data && data.by_workshop.length > 1 && (
        <div className="flex flex-wrap gap-1.5" data-testid="oro-by-workshop">
          {data.by_workshop.map((w) => (
            <button key={w.code} onClick={() => setParams({ workshop: w.code })} className="rounded border bg-card px-2 py-1 text-[11px] hover:bg-secondary transition-colors" data-testid={`oro-ws-${w.code}`}>
              <b>{w.code}</b> <span className="text-muted-foreground">{w.name}</span> · <span className="num">{w.open}</span> open{w.breached > 0 && <span className="text-red-600"> · {w.breached} late</span>}
            </button>
          ))}
        </div>
      )}

      {data && (
        <div className="flex flex-wrap items-center gap-2">
          {BUCKETS.map(([k, l]) => <Chip key={k} active={bucket === k} onClick={() => setBucket(k)} testId={`oro-bucket-${k}`}>{l}{k !== "all" && <span className="ml-1 num opacity-70">{s.buckets[k]}</span>}</Chip>)}
          <span className="mx-1 text-muted-foreground">|</span>
          <Chip active={onlyBreached} onClick={() => setOnlyBreached(!onlyBreached)} testId="oro-only-breached"><AlertTriangle size={11} className="inline mr-1" />Promise breached</Chip>
          <Select value={stage || "ALL"} onValueChange={(v) => setStage(v === "ALL" ? "" : v)}>
            <SelectTrigger className="h-7 w-[200px] text-[11px]" data-testid="oro-stage-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All stages</SelectItem>
              {s.stages.map((x) => <SelectItem key={x.stage} value={x.stage} data-testid={`oro-stage-${x.stage.replace(/\W+/g, "-")}`}>{x.stage} ({x.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="RO, reg no, model, SA, customer…" className="h-7 w-60 text-xs ml-auto" data-testid="oro-search" />
        </div>
      )}

      <div className="border bg-card rounded-md overflow-auto" data-testid="oro-table">
        <table className="w-full text-xs">
          <thead className="border-b"><tr>{["Age", "Workshop", "RO", "Vehicle", "Customer", "Work type", "Stage", "Promise", "SA", "Technician", "Delay reason", "Estimate ₹"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.dealer_code}-${r.ro_no}`} className={`border-b last:border-0 hover:bg-muted/50 ${r.breached ? "bg-red-50/40" : ""}`} data-testid={`oro-row-${r.ro_no}`}>
                <td className={`px-2.5 py-1.5 num whitespace-nowrap ${ageTone(r.age_days)}`}><Clock size={11} className="inline mr-1 opacity-60" />{r.age_days != null ? `${r.age_days} d` : "—"}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{r.dealer_code} <span className="text-muted-foreground">{r.workshop}</span></td>
                <td className="px-2.5 py-1.5 font-mono whitespace-nowrap">{r.ro_no}<div className="text-[10px] text-muted-foreground">{fmtDt(r.opened_dt)}</div></td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{r.reg_no}<div className="text-[10px] text-muted-foreground">{r.model}{r.revisit && <span className="ml-1 rounded bg-amber-100 text-amber-900 px-1 font-semibold">revisit</span>}</div></td>
                <td className="px-2.5 py-1.5 max-w-[160px] truncate" title={r.customer}>{r.customer}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{r.work_type}{r.service_type && <span className="text-muted-foreground"> · {r.service_type}</span>}{r.insurance && <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">{r.insurance}</div>}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{r.stage || "—"}{r.sub_status && <div className="text-[10px] text-muted-foreground">{r.sub_status}</div>}{r.status === "Billed & not Delivered" && <div className="text-[10px] text-amber-700 font-semibold">billed, not delivered</div>}</td>
                <td className={`px-2.5 py-1.5 whitespace-nowrap ${r.breached ? "text-red-600 font-semibold" : ""}`}>{fmtDt(r.promise_dt)}{r.promise_revised && <span className="ml-1 text-[10px] text-muted-foreground">(revised)</span>}{r.breached && <div className="text-[10px]">overdue {Math.round(r.overdue_hours)} h</div>}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{r.sa}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap text-muted-foreground">{r.tech}</td>
                <td className={`px-2.5 py-1.5 max-w-[220px] truncate ${!r.delay_reason && r.breached ? "text-red-600 font-semibold" : "text-muted-foreground"}`} title={r.delay_reason}>{r.delay_reason || (r.breached ? "no reason recorded" : "—")}</td>
                <td className="px-2.5 py-1.5 num">{fmtINR(r.estimate)}</td>
              </tr>
            ))}
            {data && rows.length === 0 && <tr><td colSpan={12} className="px-3 py-8 text-center text-muted-foreground">No open ROs match these filters</td></tr>}
          </tbody>
        </table>
      </div>
      {workshop && <Link to={`/service/workshop/${workshop}`} className="text-xs underline" data-testid="oro-to-workshop">← {workshop} workshop dashboard</Link>}
    </div>
  );
}
